import { changeLanguage } from '@gripp/i18n';
import {
  Feature,
  Graphback,
  isFeatureEnabledForContext,
  useAuthentication,
  useCurrentAccount,
  useCurrentWorkspace,
  useFeatures,
  useSetDefaultPreferredLanguage,
  useStorageKeys,
} from '@gripp/shared-logic';
import * as Sentry from '@sentry/react';
import { FC, useEffect, useState } from 'react';
import { Navigate, Outlet, useMatches, useNavigate } from 'react-router-dom';
import { Layout } from '../../dashboard/components/layout';
import { usePathWithParams } from '../../shared/hooks/usePathWithParams';

export const ProtectedRoute: FC = () => {
  const authentication = useAuthentication();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    authentication?.isAuthenticated || false
  );
  const [isAuthorized, setIsAuthorized] = useState<boolean | undefined>(
    undefined
  );
  const matches = useMatches();
  const path = usePathWithParams();
  const { account } = useCurrentAccount();
  const currentWorkspace = useCurrentWorkspace();
  const { data: featuresData, isLoading: featuresLoading } = useFeatures();
  const navigate = useNavigate();
  const storageKeys = useStorageKeys();
  const setDefaultPreferredLanguage = useSetDefaultPreferredLanguage();

  if (authentication?.isAuthenticated && authentication.accessToken) {
    Graphback.initialize(authentication.accessToken);
  }

  useEffect(() => {
    if (!authentication || authentication.isLoading) return;
    if (authentication.isAuthenticated) {
      setIsAuthenticated(true);
      return;
    }

    if (authentication && !authentication.isAuthenticated) {
      localStorage.setItem(storageKeys.redirectPath, path);
    }

    navigate('/login');
  }, [authentication, navigate, path, storageKeys.redirectPath]);

  useEffect(() => {
    if (!isAuthenticated || !account) return;

    const requiredPermission = matches
      .map((match) => (match.handle as any)?.requiredPermission)
      .filter((x) => x)
      .pop();
    const requiredFeature = matches
      .map((match) => (match.handle as any)?.requiredFeature)
      .filter((x) => x)
      .pop();

    if (!account.hasPermission(requiredPermission)) {
      setIsAuthorized(false);
      return;
    }

    if (!requiredFeature) {
      setIsAuthorized(true);
      return;
    }

    // A feature-gated route must wait for the features query to settle before
    // deciding — a flag reads as disabled while loading (see
    // gripp-workspace/AGENTS.md "Feature flags read as disabled while
    // loading"), so deciding early would flash-redirect an enabled workspace
    // to /forbidden on a direct-URL hit. Stay in the `undefined`
    // (Authorizing) state until the features query resolves, same as the
    // permission check above waiting on `account`.
    if (featuresLoading || !featuresData) {
      setIsAuthorized(undefined);
      return;
    }

    const features: Feature[] = featuresData.items ?? [];
    const feature = features.find((f) => f.name === requiredFeature);
    setIsAuthorized(
      isFeatureEnabledForContext(feature, currentWorkspace?.id, account.id)
    );
  }, [
    account,
    isAuthenticated,
    matches,
    featuresData,
    featuresLoading,
    currentWorkspace,
  ]);

  useEffect(() => {
    const onInit = async () => {
      if (!account) return;
      //TODO: init

      if (account.preferences?.preferredLanguage) {
        await changeLanguage(account.preferences.preferredLanguage);
      } else {
        await setDefaultPreferredLanguage(account);
      }

      Sentry.setTag('account_id', account.id);
    };

    onInit();
  }, [account]);

  if (isAuthorized === undefined) {
    return <Authorizing />;
  }

  return isAuthorized ? <PrivateRouterLayout /> : <Navigate to="/forbidden" />;
};

const PrivateRouterLayout = () => (
  <Layout>
    <Outlet />
  </Layout>
);

const Authorizing = () => <h3>Authorizing...</h3>;
